import React, { useState, useEffect, useMemo } from 'react'
import { motion } from 'framer-motion'
import { PipeDataStructure, CalculatedMetrics } from './types/pipe'
import PolarRadiusChart from './components/PolarRadiusChart'
import LinearChart from './components/LinearChart'
import Pipe3DVisualization from './components/Pipe3DVisualization'
import {
  Dialog,
  DialogContent,
  DialogContentWithoutClose,
  DialogHeader,
  DialogTitle,
} from '../../../../../../components/ui/dialog'
import { Button } from '../../../../../../components/ui/button'
import { Switch } from '../../../../../../components/ui/switch'
import { ArrowLeft, Download, Eye } from 'lucide-react'
import './index.css'
import { DashboardProject } from '@/features/project/types/project'
import { loadPipeDataForEnd, type PipeEndDataEntry } from './pipeDataLoader'

interface PipeFfitterProps {
  isOpen: boolean;
  onClose: () => void;
  selectedMaterial: any;
  selectedPipe: any;
  project: DashboardProject;
  unit: string;
  defaultEnd?: 'EndA' | 'EndB';
  isPipeFitterOpen: boolean;
  /** Row from getPipeStatusList — pipeData[] holds EndA/EndB fileUrl entries */
  pipeStatusRow?: { pipeData?: PipeEndDataEntry[]; pipeID?: string } | null;
}

const PipeFfitter: React.FC<PipeFfitterProps> = ({ isOpen, onClose, project, selectedMaterial, selectedPipe, unit, defaultEnd = 'EndA', isPipeFitterOpen, pipeStatusRow = null }) => {
  const [data, setData] = useState<PipeDataStructure | null>(null)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [calculatedMetrics, setCalculatedMetrics] = useState<CalculatedMetrics | null>(null)
  const [showAllInfo, setShowAllInfo] = useState(false)
  const [isStartEndDataOpen, setIsStartEndDataOpen] = useState(defaultEnd === 'EndB') // false = EndA, true = EndB
  const [qualityThreshold, setQualityThreshold] = useState(1.5) // %
  const [isJsonViewOpen, setIsJsonViewOpen] = useState(false)

  const pipeJsonFileName = useMemo(() => {
    const base =
      data?.pipeinfo?.pipe_name ||
      selectedMaterial?.details?.pipeNRI ||
      pipeStatusRow?.pipeID ||
      'pipe-data'
    const end = isStartEndDataOpen ? 'EndB' : 'EndA'
    const safe = String(base).replace(/[^\w.-]+/g, '_')
    return `${safe}-${end}.json`
  }, [data, selectedMaterial, pipeStatusRow, isStartEndDataOpen])

  const handleDownloadPipeJson = () => {
    if (!data) return
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' })
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = pipeJsonFileName
    link.click()
    window.URL.revokeObjectURL(url)
  }

  useEffect(() => {
    if (isOpen) {
      setIsStartEndDataOpen(defaultEnd === 'EndB')
    } else {
      setIsJsonViewOpen(false)
    }
  }, [isOpen, defaultEnd])

  const pipeDataEntries = useMemo((): PipeEndDataEntry[] | null => {
    const statusPipeData = pipeStatusRow?.pipeData;
    if (Array.isArray(statusPipeData) && statusPipeData.length > 0) {
      return statusPipeData;
    }
    const selectedPipeData = selectedPipe?.pipeData;
    if (Array.isArray(selectedPipeData) && selectedPipeData.length > 0) {
      return selectedPipeData;
    }
    return null;
  }, [pipeStatusRow, selectedPipe]);

  // Helper function to format timestamp — date as 19-Dec-2026
  const formatTimestamp = (timestamp: number): string => {
    const date = new Date(timestamp * 1000) // Convert Unix timestamp to milliseconds
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
    const day = date.getDate().toString().padStart(2, '0')
    const dateLabel = `${day}-${months[date.getMonth()]}-${date.getFullYear()}`
    const hours = date.getHours().toString().padStart(2, '0')
    const minutes = date.getMinutes().toString().padStart(2, '0')
    const seconds = date.getSeconds().toString().padStart(2, '0')
    const milliseconds = date.getMilliseconds().toString().padStart(3, '0')

    return `${dateLabel} ${hours}:${minutes}:${seconds}.${milliseconds}`
  }

  // Info items array for better organization
  const infoItems = data ? [
    {label:'Version' ,value :data.fileInfo.Ver ,bgColor :'bg-green-200'},
    {label:'Group ID' ,value :data.pipeinfo.group_id ,bgColor :'bg-purple-200'},
    
    // { label: 'Project', value: data.projectInfo.project_name, bgColor: 'bg-purple-100' },
    // { label: 'Number', value: data.projectInfo.project_num, bgColor: 'bg-gray-100' },
    // { label: 'Customer', value: data.projectInfo.customer_name, bgColor: 'bg-orange-100' },
    // { label: 'Manager', value: data.projectInfo.project_manager, bgColor: 'bg-yellow-100' },
    { label: 'Type', value: data.pipeinfo.type, bgColor: 'bg-blue-100' },
    { label: 'Pipe End', value: data.pipeinfo.pipe_end, bgColor: 'bg-green-100' },
    { label: 'Length', value: data.pipeinfo.pipe_length, bgColor: 'bg-red-100' ,unit: 'ft'},
    { label: 'OD', value: data.pipeinfo.pipe_OD, bgColor: 'bg-cyan-100' ,unit: 'mm'},
    { label: 'Thickness', value: data.pipeinfo.thickness, bgColor: 'bg-teal-100' ,unit: 'mm'},
    { label: 'Material', value: data.pipeinfo.material, bgColor: 'bg-indigo-100' },
    { label: 'Sample Size', value: data.pipeinfo.sample_size, bgColor: 'bg-lime-100', unit: 'points' },
    // { label: 'Seam', value: data.pipeinfo?.extreme_seam_1, bgColor: 'bg-fuchsia-100' },
    { label: 'Site Name', value: data.pipeinfo.location.site_name, bgColor: 'bg-pink-100' },
    { label: 'Latitude', value: data.pipeinfo.location.latitude, bgColor: 'bg-rose-100', unit: '°' },
    { label: 'Longitude', value: data.pipeinfo.location.longitude, bgColor: 'bg-emerald-100', unit: '°' },
    { label: 'Timestamp', value: formatTimestamp(Number(data.pipeinfo.timestamp)), bgColor: 'bg-violet-100' },
    { label: 'Technician', value: data.pipeinfo.technician_name, bgColor: 'bg-amber-100' },
    { label: 'Inner Radius Data', value: data.pipeinfo.inner_radius_data ? 'Yes' : 'No', bgColor: 'bg-sky-100' },
    { label: 'Outer Radius Data', value: data.pipeinfo.outer_radius_data ? 'Yes' : 'No', bgColor: 'bg-stone-100' },
    { label: 'Spread', value: data.pipeinfo.spread, bgColor: 'bg-slate-100' },
    { label: 'Sub Spread', value: data.pipeinfo.subSpread, bgColor: 'bg-zinc-100' }
  ] : []

  const visibleItems = showAllInfo ? infoItems : infoItems.slice(0, 7)

  /** Standards check from ovality: Pass when ovality ≤ threshold. Uses internal only, external only, or both based on available data. */
  const standardsCheckPass = useMemo((): boolean => {
    if (!calculatedMetrics) return false
    const hasInner = data?.pipeinfo?.inner_radius_data === true
    const hasOuter = data?.pipeinfo?.outer_radius_data === true
    if (hasInner && hasOuter) {
      return (
        Number.isFinite(calculatedMetrics.ovalityInternal) &&
        calculatedMetrics.ovalityInternal <= qualityThreshold &&
        Number.isFinite(calculatedMetrics.ovalityExternal) &&
        calculatedMetrics.ovalityExternal <= qualityThreshold
      )
    }
    if (hasInner)
      return Number.isFinite(calculatedMetrics.ovalityInternal) && calculatedMetrics.ovalityInternal <= qualityThreshold
    if (hasOuter)
      return Number.isFinite(calculatedMetrics.ovalityExternal) && calculatedMetrics.ovalityExternal <= qualityThreshold
    return false
  }, [calculatedMetrics, data?.pipeinfo?.inner_radius_data, data?.pipeinfo?.outer_radius_data, qualityThreshold])

  // Parse array to numbers (API/JSON may return strings e.g. "234.32"); safe when arr is undefined
  const toNumbers = (arr: (string | number)[] | undefined): number[] =>
    Array.isArray(arr) ? arr.map((v) => Number(v)).filter((v) => !Number.isNaN(v)) : []

  // Calculate metrics from the data
  const calculateMetrics = (data: PipeDataStructure): CalculatedMetrics => {
    // 
    const inner_radius = data.Data?.inner_radius
    const outer_radius = data.Data?.outer_radius
    const inner_radius_numbers = toNumbers(inner_radius)
    const outer_radius_numbers = toNumbers(outer_radius)
    const wallThickness =
      Array.isArray(inner_radius_numbers) && Array.isArray(outer_radius_numbers)
        ? inner_radius_numbers
            .map((_, i) => {
              const a = Number(inner_radius_numbers[i])
              const b = Number(outer_radius_numbers[i] ?? NaN)
              return !Number.isNaN(a) && !Number.isNaN(b) ? b - a : NaN
            })
            .filter((v) => !Number.isNaN(v))
        : []

    const nInner_radius_length = inner_radius_numbers.length
    const nOuter_radius_length = outer_radius_numbers.length
    const nWall = wallThickness.length

    const avgInternal = nInner_radius_length > 0 ? inner_radius_numbers.reduce((sum, val) => sum + val, 0) / nInner_radius_length : NaN
    const avgExternal = nOuter_radius_length > 0 ? outer_radius_numbers .reduce((sum, val) => sum + val, 0) / nOuter_radius_length : NaN
    const avgWall = nWall > 0 ? wallThickness.reduce((sum, val) => sum + val, 0) / nWall : NaN

    const stdDevInternal = nInner_radius_length > 0 ? Math.sqrt(inner_radius_numbers.reduce((sum, val) => sum + Math.pow(val - avgInternal, 2), 0) / nInner_radius_length) : NaN
    const stdDevExternal = nOuter_radius_length > 0 ? Math.sqrt(outer_radius_numbers.reduce((sum, val) => sum + Math.pow(val - avgExternal, 2), 0) / nOuter_radius_length) : NaN
    const stdDevWall = nWall > 0 ? Math.sqrt(wallThickness.reduce((sum, val) => sum + Math.pow(val - avgWall, 2), 0) / nWall) : NaN

    const minInternal = nInner_radius_length > 0 ? Math.min(...inner_radius_numbers) : NaN
    const maxInternal = nInner_radius_length > 0 ? Math.max(...inner_radius_numbers) : NaN
    const minExternal = nOuter_radius_length > 0 ? Math.min(...outer_radius_numbers) : NaN
    const maxExternal = nOuter_radius_length > 0 ? Math.max(...outer_radius_numbers) : NaN
    const minWall = nWall > 0 ? Math.min(...wallThickness) : NaN
    const maxWall = nWall > 0 ? Math.max(...wallThickness) : NaN
    const ovalityInternal = Number.isFinite(avgInternal) && avgInternal !== 0 ? ((maxInternal - minInternal) / avgInternal) * 100 : NaN
    const ovalityExternal = Number.isFinite(avgExternal) && avgExternal !== 0 ? ((maxExternal - minExternal) / avgExternal) * 100 : NaN

    // Diameter from 180° opposite pairs: diameter[i] = inner[i] + inner[i + 180] → 180 diameter values
    const half_internal = nInner_radius_length >= 2 ? Math.floor(nInner_radius_length / 2) : 0
    const diameters_internal = half_internal > 0
      ? Array.from({ length: half_internal }, (_, i) => inner_radius_numbers[i] + inner_radius_numbers[(i + half_internal) % nInner_radius_length])
      : []
    const minInnerDiameter = diameters_internal.length > 0 ? Math.min(...diameters_internal) : NaN
    const maxInnerDiameter = diameters_internal.length > 0 ? Math.max(...diameters_internal) : NaN
    const averageInnerDiameter = diameters_internal.length > 0 ? diameters_internal.reduce((sum, val) => sum + val, 0) / diameters_internal.length : NaN
    const stdDevInnerDiameter = diameters_internal.length > 0 ? Math.sqrt(diameters_internal.reduce((sum, val) => sum + Math.pow(val - averageInnerDiameter, 2), 0) / diameters_internal.length) : NaN
    const ovalityInnerDiameter = Number.isFinite(averageInnerDiameter) && averageInnerDiameter !== 0 ? ((maxInnerDiameter - minInnerDiameter) / averageInnerDiameter) * 100 : NaN


    const half_external = nOuter_radius_length >= 2 ? Math.floor(nOuter_radius_length / 2) : 0
    const diameters_external = half_external > 0
      ? Array.from({ length: half_external }, (_, i) => outer_radius_numbers[i] + outer_radius_numbers[(i + half_external) % nOuter_radius_length])
      : []
    const minOuterDiameter = diameters_external.length > 0 ? Math.min(...diameters_external) : NaN
    const maxOuterDiameter = diameters_external.length > 0 ? Math.max(...diameters_external) : NaN
    const averageOuterDiameter = diameters_external.length > 0 ? diameters_external.reduce((sum, val) => sum + val, 0) / diameters_external.length : NaN
    const stdDevOuterDiameter = diameters_external.length > 0 ? Math.sqrt(diameters_external.reduce((sum, val) => sum + Math.pow(val - averageOuterDiameter, 2), 0) / diameters_external.length) : NaN
    const ovalityOuterDiameter = Number.isFinite(averageOuterDiameter) && averageOuterDiameter !== 0 ? ((maxOuterDiameter - minOuterDiameter) / averageOuterDiameter) * 100 : NaN

  

    return {
      averageInternalRadius: avgInternal,
      averageExternalRadius: avgExternal,
      averageWallThickness: avgWall,
      minInternalRadius: minInternal,
      maxInternalRadius: maxInternal,
      minExternalRadius: minExternal,
      maxExternalRadius: maxExternal,
      minWallThickness: minWall,
      maxWallThickness: maxWall,
      ovalityInternal,
      ovalityExternal,
      minInnerDiameter,
      maxInnerDiameter,
      averageInnerDiameter,
      stdDevInnerDiameter,
      ovalityInnerDiameter,
      minOuterDiameter,
      maxOuterDiameter,
      averageOuterDiameter,
      stdDevOuterDiameter,
      ovalityOuterDiameter,
      stdDevInternal,
      stdDevExternal,
      stdDevWall
    }
  }

  // Load pipe data from material details or pipe status fileUrl (EndA / EndB)
  useEffect(() => {
    if (!isOpen) return;

    const endType = isStartEndDataOpen ? 'EndB' : 'EndA';
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setLoadError(null);

      try {
        const extractedData = await loadPipeDataForEnd({
          material: isPipeFitterOpen ? undefined : selectedMaterial,
          pipeDataEntries,
          endType,
        });

        if (cancelled) return;

        if (extractedData) {
          setData(extractedData);
          const metrics = calculateMetrics(extractedData);
          setCalculatedMetrics(metrics);
        } else {
          setData(null);
          setCalculatedMetrics(null);
        }
      } catch (error) {
        if (cancelled) return;
        setData(null);
        setCalculatedMetrics(null);
        setLoadError(error instanceof Error ? error.message : 'Failed to load pipe data');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, [isOpen, selectedMaterial, isStartEndDataOpen, pipeDataEntries, isPipeFitterOpen]);

  if (loading) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContentWithoutClose className="max-w-[100vw] w-screen h-screen max-h-screen p-0 gap-0 overflow-y-auto">
          <div className="pipe-ffitter-container loading">
            <div className="flex items-center justify-center space-x-3">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#EB1C37]"></div>
              <p className="text-lg font-medium text-gray-700">Loading Pipe Dimensional Data...</p>
            </div>
          </div>
        </DialogContentWithoutClose>
      </Dialog>
    )
  }

  return (
    <>
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContentWithoutClose className="max-w-[100vw] w-screen h-screen max-h-screen p-0 gap-0 overflow-y-auto">
        <div className="flex flex-col h-full">
          <DialogHeader className="px-4 sm:px-6 py-4 border-b flex flex-row items-center justify-between bg-white flex-shrink-0">
            <div className="flex items-center gap-3 sm:gap-4 min-w-0 flex-1">
              <Button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0"
                variant="outline"
              >
                <ArrowLeft className="h-4 w-4 sm:h-5 sm:w-5 text-gray-500" />
              </Button>
              <div className="min-w-0 flex-1">
                <DialogTitle className="text-lg sm:text-xl lg:text-2xl font-bold truncate">
                  {data ? data.pipeinfo.pipe_name : selectedMaterial?.details?.pipeNRI || 'Pipe Data'}
                </DialogTitle>
                <div className="text-xs sm:text-sm text-gray-600 mt-1 space-y-1">
                  <div className="flex flex-wrap gap-2">
                    {visibleItems.map((item, index) => (
                      <span key={index} className={`${item.bgColor} px-2 py-1 rounded text-xs`}>
                        {item.label}: {item.value}{item.unit ? ` ${item.unit}` : ''}
                      </span>
                    ))}
                    {infoItems.length > 7 && (
                      <button
                        onClick={() => setShowAllInfo(!showAllInfo)}
                        className="bg-gray-200 hover:bg-gray-300 px-3 py-1 rounded text-xs font-medium transition-colors duration-200 flex items-center gap-1"
                      >
                        {showAllInfo ? 'Show Less' : `Show More (+${infoItems.length - 7})`}
                        <span className="text-xs">{showAllInfo ? '▲' : '▼'}</span>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-2 ml-4 flex-shrink-0">
              {data ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-9 w-9 p-0"
                  title="View JSON object"
                  aria-label="View JSON object"
                  onClick={() => setIsJsonViewOpen(true)}
                >
                  <Eye className="h-4 w-4 text-gray-600" />
                </Button>
              ) : null}
              {!isPipeFitterOpen ? (
                <>
                  <span className={`text-sm ${!isStartEndDataOpen ? 'text-blue-600 font-medium' : 'text-gray-500'}`}>
                    EndA
                  </span>
                  <Switch
                    checked={isStartEndDataOpen}
                    onCheckedChange={(checked: boolean) => setIsStartEndDataOpen(checked)}
                  />
                  <span className={`text-sm ${isStartEndDataOpen ? 'text-red-600 font-medium' : 'text-gray-500'}`}>
                    EndB
                  </span>
                </>
              ) : null}
            </div>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto p-4 sm:p-6">
            {!data ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <div className="text-gray-500 text-lg mb-2">No pipe data available</div>
                  <div className="text-gray-400 text-sm">
                    {loadError
                      ? loadError
                      : `${isStartEndDataOpen ? 'EndB' : 'EndA'} data not found for this pipe`}
                  </div>
                </div>
              </div>
            ) : (
              <motion.div 
                className="pipe-ffitter-container"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
              >
                <div className=" space-y-6 w-full">
                {/* Statistical Analysis Cards */}
                {calculatedMetrics && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-2 w-full">
                    {/* Internal Radius - commented out
                    {data?.pipeinfo?.inner_radius_data && (
                    <motion.div
                      className="bg-gray-100 rounded-lg border border-gray-300 p-1.5 sm:p-2 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300 cursor-pointer w-full"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.1 }}
                      whileHover={{ scale: 1.02 }}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="text-sm sm:text-base font-semibold text-gray-900">Internal Radius</h3>
                        <span className="bg-blue-500 text-white text-xs font-bold px-2 py-1 rounded-full shadow-sm">IR</span>
                      </div>
                      <div className="grid grid-cols-4 gap-1 w-full">
                        <div className="bg-white rounded p-1 text-center shadow-sm border border-gray-200 flex-1 min-w-0">
                          <div className="text-xs text-gray-600 font-medium mb-0">Min</div>
                          <div className="text-xs font-bold text-gray-900">{calculatedMetrics.minInternalRadius.toFixed(2)}</div>
                          <div className="text-xs text-gray-500">{unit}</div>
                        </div>
                        <div className="bg-white rounded p-1 text-center shadow-sm border border-gray-200 flex-1 min-w-0">
                          <div className="text-xs text-gray-600 font-medium mb-0">Avg</div>
                          <div className="text-xs font-bold text-gray-900">{calculatedMetrics.averageInternalRadius.toFixed(2)}</div>
                          <div className="text-xs text-gray-500">{unit}</div>
                        </div>
                        <div className="bg-white rounded p-1 text-center shadow-sm border border-gray-200 flex-1 min-w-0">
                          <div className="text-xs text-gray-600 font-medium mb-0">Max</div>
                          <div className="text-xs font-bold text-gray-900">{calculatedMetrics.maxInternalRadius.toFixed(2)}</div>
                          <div className="text-xs text-gray-500">{unit}</div>
                        </div>
                        <div className="bg-white rounded p-1 text-center shadow-sm border border-gray-200 flex-1 min-w-0">
                          <div className="text-xs text-gray-600 font-medium mb-0">Std Dev</div>
                          <div className="text-xs font-bold text-gray-900">{calculatedMetrics.stdDevInternal.toFixed(2)}</div>
                          <div className="text-xs text-gray-500">{unit}</div>
                        </div>
                      </div>
                    </motion.div>
                    )}
                    */}

                    {/* External Radius - commented out
                    {data?.pipeinfo?.outer_radius_data && (
                    <motion.div
                      className="bg-gray-100 rounded-lg border border-gray-300 p-1.5 sm:p-2 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300 cursor-pointer w-full"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.2 }}
                      whileHover={{ scale: 1.02 }}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="text-sm sm:text-base font-semibold text-gray-900">External Radius</h3>
                        <span className="bg-orange-500 text-white text-xs font-bold px-2 py-1 rounded-full shadow-sm">ER</span>
                      </div>
                      <div className="grid grid-cols-4 gap-1 w-full">
                        <div className="bg-white rounded p-1 text-center shadow-sm border border-gray-200 flex-1 min-w-0">
                          <div className="text-xs text-gray-600 font-medium mb-0">Min</div>
                          <div className="text-xs font-bold text-gray-900">{calculatedMetrics.minExternalRadius.toFixed(2)}</div>
                          <div className="text-xs text-gray-500">{unit}</div>
                        </div>
                        <div className="bg-white rounded p-1 text-center shadow-sm border border-gray-200 flex-1 min-w-0">
                          <div className="text-xs text-gray-600 font-medium mb-0">Avg</div>
                          <div className="text-xs font-bold text-gray-900">{calculatedMetrics.averageExternalRadius.toFixed(2)}</div>
                          <div className="text-xs text-gray-500">{unit}</div>
                        </div>
                        <div className="bg-white rounded p-1 text-center shadow-sm border border-gray-200 flex-1 min-w-0">
                          <div className="text-xs text-gray-600 font-medium mb-0">Max</div>
                          <div className="text-xs font-bold text-gray-900">{calculatedMetrics.maxExternalRadius.toFixed(2)}</div>
                          <div className="text-xs text-gray-500">{unit}</div>
                        </div>
                        <div className="bg-white rounded p-1 text-center shadow-sm border border-gray-200 flex-1 min-w-0">
                          <div className="text-xs text-gray-600 font-medium mb-0">Std Dev</div>
                          <div className="text-xs font-bold text-gray-900">{calculatedMetrics.stdDevExternal.toFixed(2)}</div>
                          <div className="text-xs text-gray-500">{unit}</div>
                        </div>
                      </div>
                    </motion.div>
                    )}
                    */}

                    {data?.pipeinfo?.inner_radius_data && (
                    <motion.div
                      className="bg-gray-100 rounded-lg border border-gray-300 p-1.5 sm:p-2 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300 cursor-pointer w-full"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.25 }}
                      whileHover={{ scale: 1.02 }}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="text-sm sm:text-base font-semibold text-gray-900">Internal Diameter</h3>
                        <span className="bg-cyan-500 text-white text-xs font-bold px-2 py-1 rounded-full shadow-sm">ID</span>
                      </div>
                      <div className="grid grid-cols-4 gap-1 w-full">
                        <div className="bg-white rounded p-1 text-center shadow-sm border border-gray-200 flex-1 min-w-0">
                          <div className="text-xs text-gray-600 font-medium mb-0">Min</div>
                          <div className="text-xs font-bold text-gray-900">{calculatedMetrics.minInnerDiameter.toFixed(2)}</div>
                          <div className="text-xs text-gray-500">{unit}</div>
                        </div>
                        <div className="bg-white rounded p-1 text-center shadow-sm border border-gray-200 flex-1 min-w-0">
                          <div className="text-xs text-gray-600 font-medium mb-0">Avg</div>
                          <div className="text-xs font-bold text-gray-900">{calculatedMetrics.averageInnerDiameter.toFixed(2)}</div>
                          <div className="text-xs text-gray-500">{unit}</div>
                        </div>
                        <div className="bg-white rounded p-1 text-center shadow-sm border border-gray-200 flex-1 min-w-0">
                          <div className="text-xs text-gray-600 font-medium mb-0">Max</div>
                          <div className="text-xs font-bold text-gray-900">{calculatedMetrics.maxInnerDiameter.toFixed(2)}</div>
                          <div className="text-xs text-gray-500">{unit}</div>
                        </div>
                        <div className="bg-white rounded p-1 text-center shadow-sm border border-gray-200 flex-1 min-w-0">
                          <div className="text-xs text-gray-600 font-medium mb-0">Std Dev</div>
                          <div className="text-xs font-bold text-gray-900">{calculatedMetrics.stdDevInnerDiameter.toFixed(2)}</div>
                          <div className="text-xs text-gray-500">{unit}</div>
                        </div>
                      </div>
                    </motion.div>
                    )}

                    {data?.pipeinfo?.outer_radius_data && (
                    <motion.div
                      className="bg-gray-100 rounded-lg border border-gray-300 p-1.5 sm:p-2 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300 cursor-pointer w-full"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.28 }}
                      whileHover={{ scale: 1.02 }}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="text-sm sm:text-base font-semibold text-gray-900">External Diameter</h3>
                        <span className="bg-amber-500 text-white text-xs font-bold px-2 py-1 rounded-full shadow-sm">ED</span>
                      </div>
                      <div className="grid grid-cols-4 gap-1 w-full">
                        <div className="bg-white rounded p-1 text-center shadow-sm border border-gray-200 flex-1 min-w-0">
                          <div className="text-xs text-gray-600 font-medium mb-0">Min</div>
                          <div className="text-xs font-bold text-gray-900">{calculatedMetrics.minOuterDiameter.toFixed(2)}</div>
                          <div className="text-xs text-gray-500">{unit}</div>
                        </div>
                        <div className="bg-white rounded p-1 text-center shadow-sm border border-gray-200 flex-1 min-w-0">
                          <div className="text-xs text-gray-600 font-medium mb-0">Avg</div>
                          <div className="text-xs font-bold text-gray-900">{calculatedMetrics.averageOuterDiameter.toFixed(2)}</div>
                          <div className="text-xs text-gray-500">{unit}</div>
                        </div>
                        <div className="bg-white rounded p-1 text-center shadow-sm border border-gray-200 flex-1 min-w-0">
                          <div className="text-xs text-gray-600 font-medium mb-0">Max</div>
                          <div className="text-xs font-bold text-gray-900">{calculatedMetrics.maxOuterDiameter.toFixed(2)}</div>
                          <div className="text-xs text-gray-500">{unit}</div>
                        </div>
                        <div className="bg-white rounded p-1 text-center shadow-sm border border-gray-200 flex-1 min-w-0">
                          <div className="text-xs text-gray-600 font-medium mb-0">Std Dev</div>
                            <div className="text-xs font-bold text-gray-900">{calculatedMetrics.stdDevOuterDiameter.toFixed(2)}</div>
                          <div className="text-xs text-gray-500">{unit}</div>
                        </div>
                      </div>
                    </motion.div>
                    )}

                    {data?.pipeinfo?.inner_radius_data && data?.pipeinfo?.outer_radius_data && (
                    <>
                    <motion.div
                      className="bg-gray-100 rounded-lg border border-gray-300 p-1.5 sm:p-2 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300 cursor-pointer w-full"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.3 }}
                      whileHover={{ scale: 1.02 }}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="text-sm sm:text-base font-semibold text-gray-900">Wall Thickness</h3>
                        <span className="bg-purple-500 text-white text-xs font-bold px-2 py-1 rounded-full shadow-sm">WT</span>
                      </div>
                      <div className="grid grid-cols-5 gap-1 w-full">
                        <div className="bg-white rounded p-1 text-center shadow-sm border border-gray-200 flex-1 min-w-0">
                          <div className="text-xs text-gray-600 font-medium mb-0">Min</div>
                          <div className="text-xs font-bold text-gray-900">{calculatedMetrics.minWallThickness.toFixed(2)}</div>
                          <div className="text-xs text-gray-500">{unit}</div>
                        </div>
                        <div className="bg-white rounded p-1 text-center shadow-sm border border-gray-200 flex-1 min-w-0">
                          <div className="text-xs text-gray-600 font-medium mb-0">Avg</div>
                          <div className="text-xs font-bold text-gray-900">{calculatedMetrics.averageWallThickness.toFixed(2)}</div>
                          <div className="text-xs text-gray-500">{unit}</div>
                        </div>
                        <div className="bg-white rounded p-1 text-center shadow-sm border border-gray-200 flex-1 min-w-0">
                          <div className="text-xs text-gray-600 font-medium mb-0">Max</div>
                          <div className="text-xs font-bold text-gray-900">{calculatedMetrics.maxWallThickness.toFixed(2)}</div>
                          <div className="text-xs text-gray-500">{unit}</div>
                        </div>
                        <div className="bg-white rounded p-1 text-center shadow-sm border border-gray-200 flex-1 min-w-0">
                          <div className="text-xs text-gray-600 font-medium mb-0">Range</div>
                          <div className="text-xs font-bold text-gray-900">{(calculatedMetrics.maxWallThickness - calculatedMetrics.minWallThickness).toFixed(2)}</div>
                          <div className="text-xs text-gray-500">{unit}</div>
                        </div>
                        <div className="bg-white rounded p-1 text-center shadow-sm border border-gray-200 flex-1 min-w-0">
                          <div className="text-xs text-gray-600 font-medium mb-0">Std Dev</div>
                          <div className="text-xs font-bold text-gray-900">{calculatedMetrics.stdDevWall.toFixed(2)}</div>
                          <div className="text-xs text-gray-500">{unit}</div>
                        </div>
                      </div>
                    </motion.div>
                    </>
                    )}
                    <motion.div
                      className="bg-gray-100 rounded-lg border border-gray-300 p-1.5 sm:p-2 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300 cursor-pointer w-full"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.4 }}
                      whileHover={{ scale: 1.02 }}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="text-sm sm:text-base font-semibold text-gray-900">Quality Assessment</h3>
                        <span className="bg-green-500 text-white text-xs font-bold px-2 py-1 rounded-full shadow-sm">QA</span>
                      </div>
                      <div className="grid grid-cols-3 gap-1 w-full">
                        {data?.pipeinfo?.inner_radius_data && (
                          <div className="bg-white rounded p-1 text-center shadow-sm border border-gray-200 flex-1 min-w-0">
                            <div className="text-xs text-gray-600 font-medium mb-0">IR Ovality</div>
                            <div className="text-xs font-bold text-gray-900">{calculatedMetrics.ovalityInternal.toFixed(2)}</div>
                            <div className="text-xs text-gray-500">%</div>
                          </div>
                        )}
                        {data?.pipeinfo?.outer_radius_data && (
                          <div className="bg-white rounded p-1 text-center shadow-sm border border-gray-200 flex-1 min-w-0">
                            <div className="text-xs text-gray-600 font-medium mb-0">ER Ovality</div>
                            <div className="text-xs font-bold text-gray-900">{calculatedMetrics.ovalityExternal.toFixed(2)}</div>
                            <div className="text-xs text-gray-500">%</div>
                          </div>
                        )}
                        {(data?.pipeinfo?.inner_radius_data || data?.pipeinfo?.outer_radius_data) && (
                          <div className="bg-white rounded p-1 text-center shadow-sm border border-gray-200 flex-1 min-w-0">
                            <div className="text-xs text-gray-600 font-medium mb-0">Standards Check</div>
                            <div className={`text-sm font-bold ${standardsCheckPass ? 'text-green-600' : 'text-red-600'}`}>
                              {standardsCheckPass ? 'Pass' : 'Fail'}
                            </div>
                            <div className="text-xs text-gray-500">API 5L</div>
                          </div>
                        )}
                      </div>
                    </motion.div>
                  
                  </div>
                )}


                {/* Charts section */}
                <div className="space-y-6">
                  {/* Linear Analysis Chart */}
                  <motion.div
                    className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6 shadow-sm"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 }}
                  >
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 text-center">Linear Measurement Analysis</h3>
                    <div className="flex-1">
                      {data && <LinearChart data={data} />}
                    </div>
                  </motion.div>

                  {/* Polar Analysis Chart - show when internal or external radius data available */}
                  {(data?.pipeinfo?.inner_radius_data || data?.pipeinfo?.outer_radius_data) && (
                  <motion.div
                    className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6 shadow-sm"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.6 }}
                  >
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 text-center">360° Polar Analysis</h3>
                    <div className="lg:h-96">
                      {data && <PolarRadiusChart data={data} />}
                    </div>
                  </motion.div>
                  )}

                  {/* 3D Visualization - Hidden but code preserved */}
                  {false && data && (() => {
                    const pipeData = data as PipeDataStructure;
                    return (
                      <motion.div
                        className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6 shadow-sm"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.7 }}
                      >
                        <h3 className="text-lg font-semibold text-gray-900 mb-4 text-center">3D Cross-Section</h3>
                        <div className="flex-1">
                          <Pipe3DVisualization data={pipeData} />
                        </div>
                      </motion.div>
                    );
                  })()}
                </div>
              </div>
            </motion.div>
            )}
          </div>
        </div>
      </DialogContentWithoutClose>
    </Dialog>

    <Dialog open={isJsonViewOpen} onOpenChange={setIsJsonViewOpen}>
      <DialogContent className="max-w-4xl w-[95vw] max-h-[85vh] flex flex-col gap-0 p-0 sm:rounded-lg">
        <DialogHeader className="px-4 sm:px-6 py-4 border-b border-[#E2E5EA] flex flex-row items-center justify-between space-y-0">
          <DialogTitle className="text-[18px] font-semibold text-blueprint-blue">
            View JSON object
          </DialogTitle>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-8 text-[12px] shrink-0"
            onClick={handleDownloadPipeJson}
            disabled={!data}
          >
            <Download className="h-4 w-4 mr-1.5" />
            Download
          </Button>
        </DialogHeader>
        <div className="p-4 sm:p-6 overflow-auto flex-1 min-h-0">
          <pre className="text-[11px] sm:text-xs leading-relaxed font-mono bg-[#F7F8FA] border border-[#E2E5EA] rounded-lg p-4 whitespace-pre-wrap break-words">
            {data ? JSON.stringify(data, null, 2) : ''}
          </pre>
        </div>
      </DialogContent>
    </Dialog>
    </>
  )
}
export default PipeFfitter
